import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import { usePublicConfig } from '../../config/demo.js';
import './financial-reconciliation-ledger.scss';

const BLUE = { color: '#005293' };
const PAGE_SIZE = 6;
const php = (v) => `₱${Number(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const STATUS_CLASS = { Matched: 'badge-success-soft', 'Signed Off': 'badge-primary', 'Discrepancy Detected': 'badge-danger-soft' };
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} mins ago`;
  if (m < 1440) return `${Math.round(m / 60)} hr ago`;
  return new Date(iso).toLocaleDateString('en-PH');
};

export default function FinancialReconciliationLedger() {
  const { mockPayments } = usePublicConfig();
  const [tab, setTab] = useState('journal');
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [jTotal, setJTotal] = useState(0);
  const [jPage, setJPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get('reference_id') || '');
  const [account, setAccount] = useState('ALL');
  const [batches, setBatches] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [exTotal, setExTotal] = useState(0);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState('');

  const [reconDate, setReconDate] = useState(today());
  const [reconResult, setReconResult] = useState(null);
  const [form, setForm] = useState({ batchRef: '', payoutDate: today(), csv: '' });
  const [formError, setFormError] = useState('');
  const [batchDetail, setBatchDetail] = useState(null);
  const [noteDialog, setNoteDialog] = useState(null); // { title, confirmLabel, run }
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');

  const flash = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 6000);
  };

  useEffect(() => { setSearch(query); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setJPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadSummary = useCallback(() => api.financeSummary().then(setSummary).catch((e) => setError(e.message)), []);
  const loadAccounts = useCallback(() => api.financeAccounts().then((r) => setAccounts(r.accounts)).catch((e) => setError(e.message)), []);
  const loadJournal = useCallback(() => api.financeJournal({ page: jPage, pageSize: PAGE_SIZE, account: account === 'ALL' ? '' : account, q: query })
    .then((r) => { setJournal(r.data); setJTotal(r.meta.total); setError(''); }).catch((e) => setError(e.message)), [jPage, account, query]);
  const loadSettlements = useCallback(() => Promise.all([api.listSettlements({ page: 1, pageSize: 5 }), api.listFinanceExceptions({ page: 1, pageSize: 8, status: 'Open' })])
    .then(([b, x]) => { setBatches(b.data); setExceptions(x.data); setExTotal(x.meta.total); }).catch((e) => setError(e.message)), []);

  useEffect(() => { loadSummary(); loadAccounts(); loadSettlements(); }, [loadSummary, loadAccounts, loadSettlements]);
  useEffect(() => { loadJournal(); }, [loadJournal]);

  const refreshAll = () => Promise.all([loadSummary(), loadSettlements(), loadJournal()]);

  const guarded = (key, fn) => async (...args) => {
    setBusy(key);
    try { await fn(...args); } catch (e) { flash(false, e.message); } finally { setBusy(''); }
  };

  const reconcile = guarded('recon', async () => {
    const r = await api.runReconciliation({ businessDate: reconDate });
    setReconResult(r);
    flash(r.status === 'Balanced', r.status === 'Balanced'
      ? `3-way reconciliation balanced for ${r.businessDate}: ${php(0)} variance.`
      : `Discrepancy Detected for ${r.businessDate}: ${php(r.totalVariance)} total variance. The Finance team was alerted.`);
    await refreshAll();
  });

  const exportCsv = guarded('export', async () => {
    const blob = await api.exportFinanceJournal({ account: account === 'ALL' ? '' : account, q: query });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'general-ledger-journal.csv';
    a.click();
    URL.revokeObjectURL(url);
    flash(true, 'General ledger journal exported to CSV.');
  });

  const verifyIntegrity = guarded('verify', async () => {
    const r = await api.verifyLedgerIntegrity();
    flash(r.status === 'Verified', r.status === 'Verified'
      ? `Ledger verified: ${r.entriesChecked} entries across ${r.chainsChecked} chains, ${r.entriesSealed} newly sealed.`
      : 'CRITICAL TAMPER ALARM: a broken hash chain was detected and payment processing was halted.');
    await refreshAll();
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, csv: String(reader.result || ''), batchRef: p.batchRef || f.name.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9._-]/g, '-') }));
    reader.readAsText(f);
  };

  const importBatch = guarded('import', async () => {
    if (!form.batchRef.trim()) { setFormError('Payout Batch ID is required.'); return; }
    if (!form.csv.trim()) { setFormError('Upload or paste the PayMongo settlement CSV.'); return; }
    setFormError('');
    try {
      const b = await api.importSettlement({ batchRef: form.batchRef.trim(), payoutDate: form.payoutDate, csv: form.csv });
      flash(b.status === 'Matched', b.status === 'Matched' ? `Batch ${b.batchRef} imported: all ${b.lineCount} payouts matched.` : `Batch ${b.batchRef} imported with ${b.exceptions.length} exception(s) flagged for investigation.`);
      setForm({ batchRef: '', payoutDate: form.payoutDate, csv: '' });
      await refreshAll();
    } catch (e) { setFormError(e.message); }
  });

  const syncBatch = guarded('sync', async () => {
    const b = await api.syncSettlement({ payoutDate: form.payoutDate });
    // On demo environments there is no PayMongo account behind this: the payout is built from the app's own top-ups.
    flash(b.status === 'Matched', mockPayments
      ? `Demo: simulated ${b.lineCount} payouts from this app's own top-ups into batch ${b.batchRef} (${b.status}). No PayMongo account was contacted.`
      : `Synced ${b.lineCount} payouts from PayMongo into batch ${b.batchRef} (${b.status}).`);
    await refreshAll();
  });

  const review = guarded('review', async (id) => setBatchDetail(await api.getSettlement(id)));

  const askNote = (cfg) => { setNote(''); setNoteError(''); setNoteDialog(cfg); };
  const confirmNote = async () => {
    if (note.trim().length < 5) { setNoteError('A note of at least 5 characters is required.'); return; }
    setBusy('note');
    try {
      await noteDialog.run(note.trim());
      setNoteDialog(null);
      await refreshAll();
    } catch (e) { setNoteError(e.message); } finally { setBusy(''); }
  };

  const signOff = (b) => askNote({
    title: `Sign off batch ${b.batchRef}`, confirmLabel: 'Sign Off & Lock Period',
    run: async (n) => { await api.signOffSettlement(b.id, { note: n }); flash(true, `Batch ${b.batchRef} signed off and period ${b.payoutDate} locked.`); setBatchDetail(null); },
  });
  const resolveEx = (x) => askNote({
    title: `Resolve exception ${x.reference}`, confirmLabel: 'Mark Resolved',
    run: async (n) => { await api.resolveFinanceException(x.id, { note: n }); flash(true, 'Exception resolved and recorded in the audit trail.'); if (batchDetail) setBatchDetail(await api.getSettlement(batchDetail.id)); },
  });
  const resume = () => askNote({
    title: 'Resume payment processing', confirmLabel: 'Re-verify & Resume',
    run: async (n) => { await api.resumePayments({ note: n }); flash(true, 'Ledger re-verified. Payment processing resumed.'); },
  });

  const tb = summary?.trialBalance;
  const halt = summary?.integrity?.halt;
  const lastRun = summary?.integrity?.lastRun;
  const jPages = Math.max(1, Math.ceil(jTotal / PAGE_SIZE));
  const rStart = jTotal === 0 ? 0 : (jPage - 1) * PAGE_SIZE + 1;
  const rEnd = Math.min(jPage * PAGE_SIZE, jTotal);

  return (
    <div className="container-fluid py-4" id="ledger-reconciliation-container">
      {toast && <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="finance-toast">{toast.msg}</div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={BLUE}><i className="fas fa-balance-scale me-2" style={BLUE} />Financial Reconciliation &amp; Double-Entry Ledger</h4>
          <p className="text-muted small mb-0">Auditable general ledger bookkeeping for charging transactions, PayMongo clearing, and automated settlement verification.</p>
        </div>
        <div className="mt-2 mt-md-0 d-flex flex-wrap align-items-center gap-2">
          <input type="date" className="form-control form-control-sm" style={{ width: 150 }} id="recon-date" aria-label="Reconciliation date" value={reconDate} onChange={(e) => setReconDate(e.target.value)} />
          <button type="button" className="btn btn-sm btn-outline-primary" id="btn-reconcile-gateway" disabled={busy === 'recon'} onClick={reconcile}>
            {busy === 'recon' ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />} Reconcile PayMongo Payouts
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-export-ledger" disabled={busy === 'export'} onClick={exportCsv}>
            <i className="fas fa-file-export me-1" /> Export Journal (CSV)
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert" id="finance-error">{error}</div>}

      {halt?.halted && (
        <div className="alert alert-danger d-flex flex-wrap justify-content-between align-items-center" role="alert" id="payments-halt-banner">
          <div><i className="fas fa-exclamation-triangle me-2" /><strong>CRITICAL TAMPER ALARM</strong> &mdash; payment processing is halted. {halt.reason}</div>
          <button type="button" className="btn btn-sm btn-danger" id="btn-resume-payments" onClick={resume}>Re-verify &amp; Resume</button>
        </div>
      )}

      {reconResult && (
        <div className="card border shadow-sm bg-white mb-4" id="recon-result">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
            <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-project-diagram me-2" />3-Way Reconciliation &mdash; {reconResult.businessDate}</h6>
            <span className={`badge ${reconResult.status === 'Balanced' ? 'badge-success-soft' : 'badge-danger-soft'}`} id="recon-status">{reconResult.status}</span>
          </div>
          <div className="card-body py-2 small">
            <div className="d-flex flex-wrap gap-4">
              <span>Leg 1 Gateway vs Top-ups: <strong id="recon-leg1">{php(reconResult.leg1Variance)}</strong></span>
              <span>Leg 2 CDRs vs Wallet debits: <strong id="recon-leg2">{php(reconResult.leg2Variance)}</strong></span>
              <span>Leg 3 Wallets vs GL 2010: <strong id="recon-leg3">{php(reconResult.leg3Variance)}</strong></span>
              <span>Total variance: <strong id="recon-total">{php(reconResult.totalVariance)}</strong></span>
            </div>
            {reconResult.exceptions.length > 0 && (
              <ul className="mt-2 mb-0" id="recon-exceptions">
                {reconResult.exceptions.map((x) => <li key={x.id}>Leg {x.leg} &middot; {x.description} ({php(x.variance)})</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-3 mb-3">
          <div className="card border shadow-sm h-100 bg-white"><div className="card-body p-3">
            <div className="text-muted small text-uppercase fw-bold">Total Charging Revenue</div>
            <div className="d-flex justify-content-between align-items-baseline mt-2">
              <h3 className="fw-bold text-success mb-0" id="stat-revenue">{php(summary?.revenue)}</h3>
              <span className="badge badge-success-soft">Recognized</span>
            </div>
          </div></div>
        </div>
        <div className="col-12 col-md-6 col-lg-3 mb-3">
          <div className="card border shadow-sm h-100 bg-white"><div className="card-body p-3">
            <div className="text-muted small text-uppercase fw-bold">Prepaid Wallet Liabilities</div>
            <div className="d-flex justify-content-between align-items-baseline mt-2">
              <h3 className="fw-bold mb-0" style={BLUE} id="stat-liabilities">{php(summary?.walletLiabilities)}</h3>
              <span className="badge bg-primary">Unearned Float</span>
            </div>
          </div></div>
        </div>
        <div className="col-12 col-md-6 col-lg-3 mb-3">
          <div className="card border shadow-sm h-100 bg-white"><div className="card-body p-3">
            <div className="text-muted small text-uppercase fw-bold">PayMongo Clearing In-Transit</div>
            <div className="d-flex justify-content-between align-items-baseline mt-2">
              <h3 className="fw-bold mb-0" style={{ color: '#d97706' }} id="stat-clearing">{php(summary?.clearingInTransit)}</h3>
              <span className="badge badge-warning-soft">Awaiting payout</span>
            </div>
          </div></div>
        </div>
        <div className="col-12 col-md-6 col-lg-3 mb-3">
          <div className="card border shadow-sm h-100 bg-white"><div className="card-body p-3">
            <div className="text-muted small text-uppercase fw-bold">Double-Entry Trial Balance</div>
            <div className="d-flex justify-content-between align-items-baseline mt-2">
              <h3 className={`fw-bold mb-0 ${tb?.balanced === false ? 'text-danger' : 'text-success'}`} id="stat-balance-check">{php(tb?.difference)}</h3>
              <span className={`badge ${tb?.balanced === false ? 'bg-danger' : 'bg-success'} fw-bold`} id="stat-balance-badge">
                <i className={`fas ${tb?.balanced === false ? 'fa-times' : 'fa-check'} me-1`} />{tb?.balanced === false ? 'Out of balance' : 'Balanced'}
              </span>
            </div>
            <div className="small text-muted mt-1" id="stat-balance-totals">Debits {php(tb?.totalDebits)} &middot; Credits {php(tb?.totalCredits)}</div>
          </div></div>
        </div>
      </div>

      <div className="card border shadow-sm bg-white mb-4" id="integrity-card">
        <div className="card-body py-2 d-flex flex-wrap justify-content-between align-items-center">
          <div className="small">
            <i className="fas fa-link me-2" style={BLUE} /><strong>Ledger hash-chain integrity:</strong>{' '}
            <span id="integrity-status" className={`badge ${lastRun?.status === 'Verified' ? 'badge-success-soft' : lastRun ? 'badge-danger-soft' : 'bg-secondary'}`}>{lastRun?.status || 'Not yet verified'}</span>
            {lastRun && <span className="text-muted ms-2" id="integrity-detail">{lastRun.entriesChecked} entries / {lastRun.chainsChecked} chains &middot; {ago(lastRun.finishedAt || lastRun.startedAt)}</span>}
            {summary?.integrity?.lastDigest && <span className="text-muted ms-2 font-monospace" id="integrity-root" title={summary.integrity.lastDigest.merkleRoot}>Sealed root {summary.integrity.lastDigest.merkleRoot.slice(0, 12)}&hellip;</span>}
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" id="btn-verify-integrity" disabled={busy === 'verify'} onClick={verifyIntegrity}>
            {busy === 'verify' ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-shield-alt me-1" />} Verify Ledger Integrity
          </button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3" id="ledgerTabs" role="tablist">
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'journal' ? 'active' : ''}`} id="tab-journal-link" role="tab" aria-selected={tab === 'journal'} onClick={() => setTab('journal')}>
            <i className="fas fa-book me-1" /> General Ledger Journal
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'settlement' ? 'active' : ''}`} id="tab-settlement-link" role="tab" aria-selected={tab === 'settlement'} onClick={() => setTab('settlement')}>
            <i className="fas fa-university me-1" /> PayMongo Gateway Settlements
            {exTotal > 0 && <span className="badge bg-danger ms-2" id="exception-count">{exTotal}</span>}
          </button>
        </li>
      </ul>

      {tab === 'journal' && (
        <div className="card border shadow-sm bg-white" id="tab-journal">
          <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
            <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-list-alt me-2" style={BLUE} />Double-Entry Journal Entries</h6>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0">
              <div className="input-group input-group-sm" style={{ width: 260 }}>
                <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
                <input type="text" className="form-control" id="search-journal" placeholder="Search Tx Ref, Description..." aria-label="Search journal" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select className="form-select form-select-sm" id="filter-account" style={{ width: 240 }} aria-label="Filter by account" value={account} onChange={(e) => { setAccount(e.target.value); setJPage(1); }}>
                <option value="ALL">All Accounts</option>
                {accounts.map((a) => <option key={a.account} value={a.account}>{a.code} {a.account}</option>)}
              </select>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0" id="table-journal">
                <thead><tr className="text-muted small text-uppercase">
                  <th>Tx Reference</th><th>Account Type</th><th>Entry Type</th><th className="text-end">Amount (PHP)</th><th className="text-end">Balance After</th><th>Narrative Description</th><th>Chain</th><th>Timestamp</th>
                </tr></thead>
                <tbody id="journal-table-body">
                  {journal.length === 0 && <tr><td colSpan={8} className="text-center p-4 text-muted">No journal lines match the selected account or search filter.</td></tr>}
                  {journal.map((e) => (
                    <tr key={e.id}>
                      <td className="font-monospace fw-bold" style={BLUE}>{e.transactionReference}</td>
                      <td><code className="text-primary fw-bold">{e.accountType}</code></td>
                      <td><span className={`badge ${e.entryType === 'DEBIT' ? 'badge-debit' : 'badge-credit'} px-2 py-1 fw-bold`}>{e.entryType}</span></td>
                      <td className="text-end font-monospace fw-bold text-dark">{php(e.amount)}</td>
                      <td className="text-end font-monospace text-muted">{e.balanceAfter == null ? '—' : php(e.balanceAfter)}</td>
                      <td className="small text-dark">{e.description}</td>
                      <td className="small text-muted"><span className="font-monospace hash-cell d-inline-block align-middle" title={e.entryHash}>#{e.chainSequence} {e.entryHash.slice(0, 8)}</span> {e.sealed && <i className="fas fa-lock text-success" title="Sealed" />}</td>
                      <td className="small text-muted">{ago(e.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
            <span className="text-muted small">Showing <strong id="journal-range-start">{rStart}</strong>–<strong id="journal-range-end">{rEnd}</strong> of <strong id="journal-count">{jTotal}</strong> journal lines</span>
            <nav aria-label="Journal pagination">
              <ul className="pagination pagination-sm mb-0" id="journal-pagination">
                {jTotal > PAGE_SIZE && (
                  <>
                    <li className={`page-item ${jPage === 1 ? 'disabled' : ''}`}><button type="button" className="page-link" aria-label="Previous page" onClick={() => setJPage(jPage - 1)}>&laquo;</button></li>
                    {Array.from({ length: Math.min(jPages, 7) }, (_, i) => {
                      const start = Math.min(Math.max(1, jPage - 3), Math.max(1, jPages - 6));
                      const p = start + i;
                      return <li key={p} className={`page-item ${p === jPage ? 'active' : ''}`}><button type="button" className="page-link" onClick={() => setJPage(p)}>{p}</button></li>;
                    })}
                    <li className={`page-item ${jPage === jPages ? 'disabled' : ''}`}><button type="button" className="page-link" aria-label="Next page" onClick={() => setJPage(jPage + 1)}>&raquo;</button></li>
                  </>
                )}
              </ul>
            </nav>
          </div>
        </div>
      )}

      {tab === 'settlement' && (
        <div id="tab-settlement">
          <div className="card border shadow-sm bg-white mb-4">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-university me-2" style={{ color: '#0284c7' }} />PayMongo Daily Bank Payout Reconciliations</h6>
              <span className="badge badge-success-soft fw-bold" id="auto-match-badge">Automated Matching: {batches.length ? `${Math.round((batches.filter((b) => b.status !== 'Discrepancy Detected').length / batches.length) * 100)}%` : 'n/a'}</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" id="table-settlements">
                  <thead><tr className="text-muted small text-uppercase">
                    <th>Payout Batch ID</th><th>Payout Date</th><th className="text-end">Gross Processed</th><th className="text-end">MDR fees</th><th className="text-end">Net Bank Deposit</th><th>Status</th><th>Discrepancy</th><th />
                  </tr></thead>
                  <tbody id="settlements-table-body">
                    {batches.length === 0 && <tr><td colSpan={8} className="text-center p-4 text-muted">No payout batches imported yet.</td></tr>}
                    {batches.map((s) => (
                      <tr key={s.id} data-batch={s.batchRef}>
                        <td className="font-monospace fw-bold" style={BLUE}>{s.batchRef}</td>
                        <td className="small text-muted">{s.payoutDate}</td>
                        <td className="text-end font-monospace text-dark fw-bold">{php(s.gross)}</td>
                        <td className="text-end font-monospace text-danger fw-bold">-{php(s.fee)}</td>
                        <td className="text-end font-monospace fw-bold text-success">{php(s.net)}</td>
                        <td><span className={`badge ${STATUS_CLASS[s.status]} batch-status`}>{s.status}</span></td>
                        <td><span className={`badge ${s.variance > 0 ? 'badge-danger-soft' : 'badge-available'}`}>{php(s.variance)}</span></td>
                        <td className="text-end text-nowrap">
                          <button type="button" className="btn btn-sm btn-outline-primary me-1 btn-review-batch" onClick={() => review(s.id)}>Review</button>
                          <button type="button" className="btn btn-sm btn-success btn-signoff-batch" disabled={s.status !== 'Matched' && !(s.status === 'Discrepancy Detected' && s.openExceptions === 0)} onClick={() => signOff(s)}>Sign Off</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-white py-2 px-3"><span className="text-muted small" id="settlement-footer">{batches.filter((b) => b.status !== 'Discrepancy Detected').length} of {batches.length} payout batches matched to the ledger</span></div>
          </div>

          <div className="row">
            <div className="col-12 col-lg-5 mb-4">
              <div className="card border shadow-sm bg-white h-100">
                <div className="card-header bg-white py-3"><h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-file-upload me-2" />Ingest PayMongo Settlement Report</h6></div>
                <div className="card-body">
                  {formError && <div className="alert alert-danger py-2 small" id="settle-error" role="alert">{formError}</div>}
                  <div className="row g-2 mb-2">
                    <div className="col-7"><label className="form-label small mb-1" htmlFor="settle-batch-ref">Payout Batch ID</label>
                      <input id="settle-batch-ref" className="form-control form-control-sm" value={form.batchRef} onChange={(e) => setForm({ ...form, batchRef: e.target.value })} placeholder="SET-20261009-PM" /></div>
                    <div className="col-5"><label className="form-label small mb-1" htmlFor="settle-date">Payout Date</label>
                      <input id="settle-date" type="date" className="form-control form-control-sm" value={form.payoutDate} onChange={(e) => setForm({ ...form, payoutDate: e.target.value })} /></div>
                  </div>
                  <label className="form-label small mb-1" htmlFor="settle-csv">Settlement CSV (payment_intent_id, gross_amount, fee_amount, net_amount)</label>
                  <textarea id="settle-csv" className="form-control form-control-sm font-monospace mb-2" rows={4} value={form.csv} onChange={(e) => setForm({ ...form, csv: e.target.value })} />
                  <input id="settle-file" type="file" accept=".csv,text/csv" className="form-control form-control-sm mb-3" aria-label="Upload settlement CSV" onChange={onFile} />
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm btn-primary" id="btn-import-settlement" disabled={busy === 'import'} onClick={importBatch}><i className="fas fa-upload me-1" />Import &amp; Match</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-sync-settlement" disabled={busy === 'sync'} onClick={syncBatch}><i className="fas fa-cloud-download-alt me-1" />{mockPayments ? 'Simulate PayMongo payout (demo)' : 'Sync from PayMongo'}</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7 mb-4">
              <div className="card border shadow-sm bg-white h-100">
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                  <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-exclamation-triangle me-2" style={{ color: '#d97706' }} />Exception Queue</h6>
                  <span className="badge bg-warning text-dark" id="exception-queue-count">{exTotal} Open</span>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0" id="table-exceptions">
                      <thead><tr className="text-muted small text-uppercase"><th>Leg</th><th>Reference</th><th>Issue</th><th className="text-end">Variance</th><th /></tr></thead>
                      <tbody id="exception-queue-body">
                        {exceptions.length === 0 && <tr><td colSpan={5} className="text-center p-4 text-muted">No open exceptions. All payouts reconcile.</td></tr>}
                        {exceptions.map((x) => (
                          <tr key={x.id} className="exception-row" data-kind={x.kind}>
                            <td>{x.leg}</td>
                            <td className="font-monospace small">{x.reference}</td>
                            <td className="small"><span className="badge badge-danger-soft me-1">{x.kind}</span>{x.description}</td>
                            <td className="text-end font-monospace fw-bold text-danger">{php(x.variance)}</td>
                            <td><button type="button" className="btn btn-sm btn-outline-secondary btn-resolve-exception" onClick={() => resolveEx(x)}>Resolve</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {batchDetail && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="batch-title" id="modal-batch">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="batch-title" style={BLUE}><i className="fas fa-university me-2" />Batch {batchDetail.batchRef} &mdash; {batchDetail.status}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setBatchDetail(null)} />
              </div>
              <div className="modal-body p-3">
                <div className="small text-muted mb-2">{batchDetail.lineCount} payout lines &middot; gross {php(batchDetail.gross)} &middot; fees {php(batchDetail.fee)} &middot; variance {php(batchDetail.variance)}</div>
                <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="table table-sm mb-0" id="table-batch-lines">
                    <thead><tr className="text-muted small text-uppercase"><th>Payment Intent</th><th className="text-end">Gross</th><th className="text-end">Fee</th><th>Match</th><th className="text-end">Variance</th></tr></thead>
                    <tbody>
                      {batchDetail.lines.map((l) => (
                        <tr key={l.paymentIntentId}>
                          <td className="font-monospace small">{l.paymentIntentId}</td><td className="text-end">{php(l.gross)}</td><td className="text-end">{php(l.fee)}</td>
                          <td><span className={`badge ${l.matchStatus === 'Matched' ? 'badge-success-soft' : 'badge-danger-soft'}`}>{l.matchStatus}</span></td><td className="text-end">{php(l.variance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setBatchDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {noteDialog && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="note-title" id="modal-note">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header"><h5 className="modal-title fw-bold" id="note-title" style={BLUE}>{noteDialog.title}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setNoteDialog(null)} /></div>
              <div className="modal-body">
                {noteError && <div className="alert alert-danger py-2 small" id="note-error" role="alert">{noteError}</div>}
                <label className="form-label small" htmlFor="note-input">Auditable note</label>
                <textarea id="note-input" className="form-control" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setNoteDialog(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" id="btn-confirm-note" disabled={busy === 'note'} onClick={confirmNote}>{noteDialog.confirmLabel}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
